import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Combobox from "../../../shared/ui/Field/Combobox";
import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Checkbox from "../../../shared/ui/Field/Checkbox";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import Text from "../../../shared/ui/Typography/Text";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";

import { searchCompanies } from "../api/companies";
import { createJoinRequest } from "../api/joinRequests";
import { fetchCompany } from "../../companyWorkspace/api/companyWorkspace";
import {
  createScopeGate,
  sameActorId,
} from "../../companyWorkspace/lib/scopeSession";
import { parseDrfFieldErrors, messageFromError } from "../api/errors";

// CompanyJoinRequestForm — feature widget. Owns ALL state for the
// multi-field composite form: combobox search, selection, message,
// confirmation, submit lifecycle, field errors. No Redux slice —
// see migration/phase11-multi-field-plan.md §10.
//
// Cross-field dependencies (all derived booleans, no engine):
//   D1: selectedCompany gates the Checkbox.
//   D2: confirmation Checkbox gates Submit.
//   D3: each field's onChange clears its own field error and _form.
// See migration/phase11-multi-field-plan.md §3.
//
// Actor lifetime: drafts/success/errors/selection reset on actor change;
// in-flight preselect/search/submit invalidated via scopeGate + request ids.

const DEBOUNCE_MS = 200;
const MESSAGE_MAX_LENGTH = 1000;
const EDITABLE_FIELDS = ["message"];

const toOption = (company) => ({
  id: company.id,
  label: company.name,
  sublabel: company.city || undefined,
  raw: company,
});

const omitKey = (obj, key) => {
  if (!obj || obj[key] === undefined) return obj || {};
  const next = { ...obj };
  delete next[key];
  return next;
};

const omitKeys = (obj, keys) => {
  if (!obj) return obj || {};
  let next = obj;
  let mutated = false;
  for (const key of keys) {
    if (next[key] !== undefined) {
      if (!mutated) {
        next = { ...obj };
        mutated = true;
      }
      delete next[key];
    }
  }
  return next;
};

function CompanyJoinRequestForm() {
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const [searchParams] = useSearchParams();
  const preselectedCompanyId = searchParams.get("companyId");

  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const submitOpRef = useRef(null);

  // --- Combobox search state ---
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [preselectState, setPreselectState] = useState(
    preselectedCompanyId ? "loading" : "idle",
  );
  const [preselectError, setPreselectError] = useState(null);
  // Bumps on cancel / send-another so preselect effect restarts even when
  // actorId + companyId query stay the same (R4-3).
  const [preselectEpoch, setPreselectEpoch] = useState(0);

  // --- Form state ---
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formActorId, setFormActorId] = useState(actorId);
  const [message, setMessage] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  // --- Submit lifecycle ---
  const [saveState, setSaveState] = useState("idle");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submittedCompanyName, setSubmittedCompanyName] = useState("");

  // --- Async coordination refs (Combobox lifecycle) ---
  const latestRequestIdRef = useRef(0);
  const debounceTimerRef = useRef(null);
  const preselectRequestRef = useRef(0);
  const selectedResourceRef = useRef(null);

  const resetDrafts = useCallback(() => {
    setQuery("");
    setOptions([]);
    setSearching(false);
    setSearchError(null);
    setSelectedCompany(null);
    selectedResourceRef.current = null;
    setMessage("");
    setConfirmed(false);
    setFieldErrors({});
    setSaveState("idle");
    setSubmittedCompanyName("");
    setPreselectError(null);
    setPreselectState(preselectedCompanyId ? "loading" : "idle");
  }, [preselectedCompanyId]);

  const submitStillApplies = useCallback((op) => {
    if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
      return false;
    }
    // Bind completion to the selected company resource — same-actor change
    // from A → B must not apply A's success/error onto B (R4-3).
    if (
      op?.resourceId != null &&
      String(selectedResourceRef.current ?? "") !== String(op.resourceId)
    ) {
      return false;
    }
    return true;
  }, [scopeGate]);

  // Actor lifetime: reset private drafts and invalidate in-flight work.
  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ actorId, companyId: null });
    submitOpRef.current = null;
    latestRequestIdRef.current += 1;
    preselectRequestRef.current += 1;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    resetDrafts();
    setFormActorId(actorId);
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      submitOpRef.current = null;
      latestRequestIdRef.current += 1;
      preselectRequestRef.current += 1;
    };
  }, [actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sameActorId(formActorId, actorId)) {
      return undefined;
    }
    if (!preselectedCompanyId) {
      setPreselectState("idle");
      setPreselectError(null);
      return undefined;
    }
    // Route/resource change must drop mid-flight submit for the prior company
    // and clear its UI busy/feedback so the new scope is usable (R5-1).
    if (submitOpRef.current) {
      scopeGate.invalidate();
      scopeGate.bindIdentity({ actorId, companyId: null });
      submitOpRef.current = null;
      setSaveState((prev) =>
        prev === "saving" || prev === "error" ? "idle" : prev,
      );
      setFieldErrors({});
    }
    selectedResourceRef.current = null;
    const requestId = ++preselectRequestRef.current;
    setPreselectState("loading");
    setPreselectError(null);
    fetchCompany(preselectedCompanyId)
      .then((data) => {
        if (preselectRequestRef.current !== requestId) return;
        if (!mountedRef.current) return;
        if (!sameActorId(formActorId, actorId)) return;
        setSelectedCompany(data);
        selectedResourceRef.current = data?.id ?? null;
        setQuery(data?.name ?? "");
        setConfirmed(false);
        setFormActorId(actorId);
        setPreselectState("ready");
      })
      .catch((err) => {
        if (preselectRequestRef.current !== requestId) return;
        if (!mountedRef.current) return;
        if (!sameActorId(formActorId, actorId)) return;
        setSelectedCompany(null);
        selectedResourceRef.current = null;
        setPreselectError(messageFromError(err));
        setPreselectState("error");
      });
    return () => {
      preselectRequestRef.current += 1;
    };
  }, [actorId, formActorId, preselectedCompanyId, preselectEpoch]);

  // ============================================================
  // ASYNC LIFECYCLE — Combobox option search
  // (mirrors features/companyFinder/widgets/CompanyFinder.js;
  //  feature-local duplication per phase plan §6.2)
  // ============================================================
  useEffect(() => {
    if (!sameActorId(formActorId, actorId)) {
      return undefined;
    }
    const trimmed = query.trim();

    // Empty query → cancel pending fetch, clear options, no spinner.
    if (trimmed.length === 0) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      latestRequestIdRef.current += 1;
      setOptions([]);
      setSearching(false);
      setSearchError(null);
      return undefined;
    }

    setSearching(true);
    setSearchError(null);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    const boundActor = actorId;

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      searchCompanies(trimmed)
        .then((data) => {
          if (latestRequestIdRef.current !== requestId) return;
          if (!mountedRef.current) return;
          if (!sameActorId(boundActor, actorId)) return;
          const results = Array.isArray(data?.results) ? data.results : [];
          setOptions(results.map(toOption));
          setSearching(false);
        })
        .catch((err) => {
          if (latestRequestIdRef.current !== requestId) return;
          if (!mountedRef.current) return;
          if (!sameActorId(boundActor, actorId)) return;
          setSearchError(messageFromError(err));
          setOptions([]);
          setSearching(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [actorId, formActorId, query]);

  // ============================================================
  // DERIVED STATE (cross-field coordination)
  // ============================================================
  const actorMatches = sameActorId(formActorId, actorId);
  const saving = saveState === "saving";

  const companyAcceptsJoin = Boolean(
    actorMatches &&
      selectedCompany &&
      selectedCompany.is_open_to_join &&
      selectedCompany.status === "active",
  );

  // D1: selectedCompany gates the Checkbox.
  const checkboxDisabled = saving || !companyAcceptsJoin;

  // D2: confirmation Checkbox + valid selection gate Submit.
  const canSubmit =
    actorMatches && !saving && companyAcceptsJoin && confirmed;

  const dirty = useMemo(
    () =>
      selectedCompany !== null
      || message !== ""
      || confirmed === true
      || query !== "",
    [selectedCompany, message, confirmed, query],
  );

  const showCompanyClosedWarning = Boolean(
    actorMatches && selectedCompany && !selectedCompany.is_open_to_join,
  );

  const showSelectionHint = Boolean(
    actorMatches && selectedCompany && query !== selectedCompany.name,
  );

  // ============================================================
  // FIELD COORDINATION (D3: change clears local + form error)
  // ============================================================
  const handleQueryChange = useCallback((event) => {
    // Typing in the combobox replaces the query but does NOT clear
    // selectedCompany — selection persists across typing per phase
    // plan §3.4.
    setQuery(event.target.value);
    setSaveState((prev) => (prev === "success" ? "idle" : prev));
  }, []);

  const handleSelect = useCallback((option) => {
    if (!option || !option.raw) return;
    // Selecting a different company invalidates any in-flight submit for the
    // previous resource without retrying the server mutation (R5-1).
    if (
      selectedResourceRef.current != null &&
      String(selectedResourceRef.current) !== String(option.raw.id)
    ) {
      scopeGate.invalidate();
      scopeGate.bindIdentity({ actorId, companyId: null });
      submitOpRef.current = null;
      setFieldErrors({});
      setSaveState((prev) =>
        prev === "saving" || prev === "error" || prev === "success"
          ? "idle"
          : prev,
      );
    } else {
      setFieldErrors((prev) => omitKey(prev, "_form"));
      setSaveState((prev) => (prev === "success" ? "idle" : prev));
    }
    setSelectedCompany(option.raw);
    selectedResourceRef.current = option.raw.id ?? null;
    setQuery(option.raw.name ?? "");
  }, [actorId, scopeGate]);

  const handleMessageChange = useCallback((event) => {
    setMessage(event.target.value);
    setFieldErrors((prev) => omitKeys(prev, ["message", "_form"]));
    setSaveState((prev) => (prev === "success" ? "idle" : prev));
  }, []);

  const handleConfirmedChange = useCallback((event) => {
    setConfirmed(event.target.checked);
    setFieldErrors((prev) => omitKey(prev, "_form"));
    setSaveState((prev) => (prev === "success" ? "idle" : prev));
  }, []);

  // ============================================================
  // SUBMIT LIFECYCLE — idle → saving → success/error
  // ============================================================
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!canSubmit || submitOpRef.current || !selectedCompany) return;

    const op = scopeGate.beginMutation({
      actorId,
      companyId: selectedCompany.id,
      resourceId: selectedCompany.id,
    });
    submitOpRef.current = op;
    setFieldErrors({});
    setSaveState("saving");

    const trimmedMessage = message.trim();
    const body = trimmedMessage.length > 0 ? { message: trimmedMessage } : {};
    const companyName = selectedCompany.name ?? "";

    try {
      await createJoinRequest(selectedCompany.id, body);
      if (!submitStillApplies(op)) {
        return;
      }
      setSubmittedCompanyName(companyName);
      setSaveState("success");
    } catch (err) {
      if (!submitStillApplies(op)) {
        return;
      }
      setFieldErrors(parseDrfFieldErrors(err, EDITABLE_FIELDS));
      setSaveState("error");
    } finally {
      if (submitOpRef.current === op) {
        submitOpRef.current = null;
      }
    }
  };

  // ============================================================
  // RESET — back to baseline (also used after success)
  // ============================================================
  const resetAll = useCallback(() => {
    scopeGate.invalidate();
    scopeGate.bindIdentity({ actorId, companyId: null });
    submitOpRef.current = null;
    setQuery("");
    setOptions([]);
    setSearching(false);
    setSearchError(null);
    setSelectedCompany(null);
    selectedResourceRef.current = null;
    setMessage("");
    setConfirmed(false);
    setFieldErrors({});
    setSaveState("idle");
    setSubmittedCompanyName("");
    setPreselectError(null);
    setPreselectState(preselectedCompanyId ? "loading" : "idle");
    // Restart preselect even when ?companyId is unchanged (R4-3).
    setPreselectEpoch((value) => value + 1);
  }, [actorId, preselectedCompanyId, scopeGate]);

  const handleReset = () => {
    if (saving) return;
    resetAll();
  };

  // Actor mismatch: never flash prior actor drafts/success.
  if (!actorMatches) {
    return (
      <div className="feature-company-join-request__state">
        <Spinner size="lg" label="Завантаження форми" />
      </div>
    );
  }

  // ============================================================
  // RENDER — success surface short-circuits the form
  // ============================================================
  if (saveState === "success") {
    return (
      <div
        className="feature-company-join-request__success"
        data-feature="company-join-request-success"
      >
        <EmptyState
          icon="fa-solid fa-circle-check"
          title="Запит надіслано"
          description={
            submittedCompanyName
              ? `Адміністратор компанії «${submittedCompanyName}» побачить ваш запит.`
              : "Адміністратор компанії побачить ваш запит."
          }
          size="md"
        />
        <div className="feature-company-join-request__success-actions">
          <button
            type="button"
            className="feature-company-join-request__reset-button"
            onClick={resetAll}
          >
            Надіслати ще один запит
          </button>
          <Link
            to="/companies/find"
            className="feature-company-join-request__back-link"
          >
            Повернутися до пошуку
          </Link>
        </div>
      </div>
    );
  }

  if (preselectState === "loading") {
    return (
      <div className="feature-company-join-request__state">
        <Spinner size="lg" label="Завантаження компанії" />
      </div>
    );
  }

  if (preselectState === "error") {
    return (
      <div className="feature-company-join-request__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося завантажити компанію"
          description={preselectError || "Спробуйте знайти компанію вручну."}
          size="md"
        />
        <Link to="/companies/find" className="feature-company-join-request__back-link">
          До пошуку
        </Link>
      </div>
    );
  }

  const formError = fieldErrors._form;
  const messageError = fieldErrors.message;
  const trimmedQuery = query.trim();
  const emptyResultsLabel =
    trimmedQuery.length > 0
      ? `Жодних компаній за запитом «${trimmedQuery}» не знайдено.`
      : undefined;

  return (
    <form
      className="feature-company-join-request__form"
      data-feature="company-join-request-form"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="feature-company-join-request__rows">
        {selectedCompany && preselectedCompanyId ? (
          <div
            className="feature-company-join-request__preselect"
            role="status"
          >
            <Text as="p" size="sm">
              Заявка до компанії «{selectedCompany.name}»
              {selectedCompany.city ? ` (${selectedCompany.city})` : ""}.
              {selectedCompany.status !== "active"
                ? " Компанія неактивна для вступу."
                : !selectedCompany.is_open_to_join
                  ? " Компанія зараз не приймає заявки."
                  : " Статус і відкритість перевірені з сервера."}
            </Text>
          </div>
        ) : null}
        <Field
          id="company-join-request-company"
          label="Компанія"
          help="Знайдіть компанію за назвою, описом або містом."
          required
        >
          <Combobox
            name="company"
            value={query}
            onChange={handleQueryChange}
            options={options}
            loading={searching}
            error={searchError}
            emptyHint="Введіть назву компанії для пошуку."
            emptyResultsLabel={emptyResultsLabel}
            onSelect={handleSelect}
            placeholder="Назва, опис або місто…"
            autoComplete="off"
            disabled={saving}
          />
        </Field>

        {showSelectionHint ? (
          <Text
            as="div"
            size="xs"
            tone="secondary"
            className="feature-company-join-request__selection-hint"
          >
            Вибрано: {selectedCompany.name}
          </Text>
        ) : null}

        {showCompanyClosedWarning ? (
          <InlineError className="feature-company-join-request__warning">
            Ця компанія не приймає запити на приєднання.
          </InlineError>
        ) : null}

        <Field
          id="company-join-request-message"
          label="Повідомлення"
          help="Коротко напишіть, чому ви хочете приєднатися (необов'язково)."
          error={messageError}
        >
          <TextInput
            name="message"
            type="text"
            value={message}
            onChange={handleMessageChange}
            placeholder="Наприклад: маю досвід у комерційній нерухомості…"
            maxLength={MESSAGE_MAX_LENGTH}
            autoComplete="off"
            disabled={saving}
          />
        </Field>

        <div className="feature-company-join-request__confirm">
          <Checkbox
            id="company-join-request-confirmed"
            name="confirmed"
            label="Підтверджую: компанія побачить моє ім'я, контакти та цей запит."
            checked={confirmed}
            onChange={handleConfirmedChange}
            disabled={checkboxDisabled}
          />
        </div>
      </div>

      <div className="feature-company-join-request__messages">
        {formError ? (
          <InlineError className="feature-company-join-request__message">
            {formError}
          </InlineError>
        ) : null}
      </div>

      <div className="feature-company-join-request__actions">
        <button
          type="button"
          className="feature-company-join-request__reset-button"
          onClick={handleReset}
          disabled={!dirty || saving}
        >
          Скасувати
        </button>
        <button
          type="submit"
          className="feature-company-join-request__submit-button"
          disabled={!canSubmit}
          data-loading={saving ? "true" : undefined}
        >
          {saving ? (
            <Spinner size="sm" label="Надсилання" />
          ) : (
            "Надіслати запит"
          )}
        </button>
      </div>
    </form>
  );
}

export default CompanyJoinRequestForm;
